import { useTranslation } from 'react-i18next';
import { useDocumentParams } from 'app/router/params';
import { Typography } from 'app/components';
import { IconButton } from 'shared/ui/IconButton/IconButton';
import { AddUser } from '../AddUser/AddUser';
import Tippy from '@tippyjs/react';
import { RemoveUser } from '../RemoveUser/RemoveUser';

import { useGetPermissions, useAddPermissionToUser, useRemovePermissionFromUser } from 'app/api/permission-queries';
import { useUsersInOrganization } from 'entities/user';
import { useUser } from 'app/state/contexts/UserContext';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import './TagPermissions.scss';

interface User {
  id: number;
  firstName: string;
  lastName: string;
  userName: string;
}

const TagPermissions = () => {
  const { t } = useTranslation(['document-permissions', 'tag']);

  const { documentId } = useDocumentParams();
  const userContext = useUser();
  const { data: permissions } = useGetPermissions(documentId);
  const { data: users = [] } = useUsersInOrganization(userContext?.selectedOrganization ?? '') as { data: User[] };
  const { mutate: addPermission } = useAddPermissionToUser();
  const { mutate: removePermission } = useRemovePermissionFromUser();

  const availableRoles = [
    {
      permission: 'TAG_ADMIN',
      name: t('tag:admin.label'),
    },
    {
      permission: 'TAG_EDITOR',
      name: t('tag:editor.label'),
    },
    {
      permission: 'TAG_VIEWER',
      name: t('tag:viewer.label'),
    },
  ];

  const addUserPermission = (userName: string, permission: string) => {
    if (!documentId) return;

    const user = users.find((u) => u.userName === userName);
    if (!user) {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: t('document-permissions:add-user.not-found'),
        removeTime: 4000,
      });
      return;
    }

    addPermission({
      documentId,
      permission,
      userId: user.id,
    });
  };

  const removeUserPermission = (userId: number, permission: string) => {
    if (!documentId) return;

    removePermission({
      documentId,
      permission,
      userId,
    });
  };

  return (
    <div className="tag-access">
      {availableRoles.map((role) => (
        <div key={role.name}>
          <div className="tag-access__header">
            <Typography variant="body-8" fontWeight="semibold">
              {role.name}
            </Typography>

            <Tippy
              trigger="click"
              placement="bottom"
              offset={[20, 10]}
              interactive
              content={<AddUser onClick={(userName) => addUserPermission(userName, role.permission)} />}
            >
              <span>
                <IconButton icon="add" />
              </span>
            </Tippy>
          </div>

          <div>
            <table className="tag-access__permissions">
              <tbody>
                {permissions && permissions.userDocumentPermissions.filter((permissions) => permissions.permission === role.permission).length > 0 ? (
                  permissions.userDocumentPermissions
                    .filter((permissions) => permissions.permission === role.permission)
                    .map((member) => {
                      const user = users.find((user) => user.id === member.userId);

                      if (!user) return null;

                      const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
                      const fullName = `${user.firstName} ${user.lastName}`;

                      return (
                        <tr key={user.id}>
                          <td>
                            <span className="initials">{initials}</span>
                            {fullName}
                            <Tippy
                              placement="bottom"
                              trigger="click"
                              offset={[20, 10]}
                              interactive
                              content={<RemoveUser onClick={() => removeUserPermission(user.id, role.permission)} />}
                            >
                              <span>
                                <IconButton icon="menu" className="icon-btn" variant="transparent" />
                              </span>
                            </Tippy>
                          </td>
                        </tr>
                      );
                    })
                ) : (
                  <tr>
                    <td>{t('tag:access.no.user')} </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TagPermissions;
